import { supabase } from '../lib/supabase';
import { approvalWorkflowService } from './approvalWorkflowService';
import aiLetterGenerationService from './aiLetterGenerationService';

export const letterRequestService = {
  // ============================================================================
  // LETTER REQUESTS
  // ============================================================================

  async createLetterRequest(employeeId, letterPurpose, additionalDetails = {}) {
    try {
      // 1. Generate letter content using AI
      const aiResult = await aiLetterGenerationService?.generateLetter(
        employeeId,
        letterPurpose,
        additionalDetails
      );

      // 2. Create letter request
      const { data: letterRequest, error: insertError } = await supabase
        ?.from('letter_requests')
        ?.insert({
          employee_id: employeeId,
          letter_purpose: letterPurpose,
          letter_status: 'draft',
          generated_content: aiResult?.content,
          ai_model_used: aiResult?.metadata?.model,
          generation_metadata: aiResult?.metadata,
          additional_details: additionalDetails,
        })
        ?.select()
        ?.single();

      if (insertError) throw insertError;

      return {
        id: letterRequest?.id,
        letterPurpose: letterRequest?.letter_purpose,
        letterStatus: letterRequest?.letter_status,
        generatedContent: letterRequest?.generated_content,
        createdAt: letterRequest?.created_at,
      };
    } catch (error) {
      console.error('Error creating letter request:', error);
      throw error;
    }
  },

  async submitLetterForApproval(letterRequestId, employeeId) {
    try {
      // 1. Update letter status to pending approval
      const { data: letterRequest, error: updateError } = await supabase
        ?.from('letter_requests')
        ?.update({ letter_status: 'pending_approval' })
        ?.eq('id', letterRequestId)
        ?.eq('employee_id', employeeId)
        ?.select()
        ?.single();

      if (updateError) throw updateError;

      // 2. Initiate approval workflow
      const approvalMetadata = {
        letter_purpose: letterRequest?.letter_purpose,
        employee_id: employeeId,
        letter_request_id: letterRequestId,
      };

      const approvalInstanceId = await approvalWorkflowService?.initiateApprovalWorkflow(
        'letter_request',
        letterRequestId,
        approvalMetadata,
        employeeId
      );

      // 3. Update letter request with approval instance ID
      await supabase
        ?.from('letter_requests')
        ?.update({ approval_instance_id: approvalInstanceId })
        ?.eq('id', letterRequestId);

      return {
        id: letterRequest?.id,
        letterStatus: 'pending_approval',
        approvalInstanceId,
      };
    } catch (error) {
      console.error('Error submitting letter for approval:', error);
      throw error;
    }
  },

  async getEmployeeLetterRequests(employeeId) {
    try {
      const { data, error } = await supabase
        ?.from('letter_requests')
        ?.select('*')
        ?.eq('employee_id', employeeId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        letterPurpose: row?.letter_purpose,
        letterStatus: row?.letter_status,
        generatedContent: row?.generated_content,
        aiModelUsed: row?.ai_model_used,
        approvedBy: row?.approved_by,
        approvedAt: row?.approved_at,
        rejectionReason: row?.rejection_reason,
        hrSignatureName: row?.hr_signature_name,
        hrSignatureTitle: row?.hr_signature_title,
        hrSignatureDate: row?.hr_signature_date,
        downloadedAt: row?.downloaded_at,
        downloadCount: row?.download_count,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching employee letter requests:', error);
      throw error;
    }
  },

  async getLetterRequestById(letterRequestId) {
    try {
      const { data, error } = await supabase
        ?.from('letter_requests')
        ?.select(`
          *,
          employees (
            first_name,
            last_name,
            email,
            position,
            department
          )
        `)
        ?.eq('id', letterRequestId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        employeeId: data?.employee_id,
        employeeName: `${data?.employees?.first_name} ${data?.employees?.last_name}`,
        employeeEmail: data?.employees?.email,
        employeePosition: data?.employees?.position,
        employeeDepartment: data?.employees?.department,
        letterPurpose: data?.letter_purpose,
        letterStatus: data?.letter_status,
        generatedContent: data?.generated_content,
        aiModelUsed: data?.ai_model_used,
        approvedBy: data?.approved_by,
        approvedAt: data?.approved_at,
        rejectionReason: data?.rejection_reason,
        hrSignatureName: data?.hr_signature_name,
        hrSignatureTitle: data?.hr_signature_title,
        hrSignatureDate: data?.hr_signature_date,
        downloadedAt: data?.downloaded_at,
        downloadCount: data?.download_count,
        createdAt: data?.created_at,
      };
    } catch (error) {
      console.error('Error fetching letter request:', error);
      throw error;
    }
  },

  async trackLetterDownload(letterRequestId) {
    try {
      const { data, error } = await supabase
        ?.from('letter_requests')
        ?.update({
          downloaded_at: new Date()?.toISOString(),
          download_count: supabase?.raw('download_count + 1'),
          letter_status: 'downloaded',
        })
        ?.eq('id', letterRequestId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        downloadCount: data?.download_count,
        downloadedAt: data?.downloaded_at,
      };
    } catch (error) {
      console.error('Error tracking letter download:', error);
      throw error;
    }
  },

  // ============================================================================
  // HR MANAGER APPROVAL
  // ============================================================================

  async getPendingLetterApprovals() {
    try {
      const { data, error } = await supabase
        ?.from('letter_requests')
        ?.select(`
          *,
          employees (
            first_name,
            last_name,
            email,
            position,
            department,
            employee_number
          )
        `)
        ?.eq('letter_status', 'pending_approval')
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        employeeId: row?.employee_id,
        employeeName: `${row?.employees?.first_name} ${row?.employees?.last_name}`,
        employeeNumber: row?.employees?.employee_number,
        employeeEmail: row?.employees?.email,
        employeePosition: row?.employees?.position,
        employeeDepartment: row?.employees?.department,
        letterPurpose: row?.letter_purpose,
        letterStatus: row?.letter_status,
        generatedContent: row?.generated_content,
        createdAt: row?.created_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching pending letter approvals:', error);
      throw error;
    }
  },

  async approveLetterRequest(letterRequestId, hrManagerId, signatureName, signatureTitle) {
    try {
      const { data, error } = await supabase
        ?.from('letter_requests')
        ?.update({
          letter_status: 'approved',
          approved_by: hrManagerId,
          approved_at: new Date()?.toISOString(),
          hr_signature_name: signatureName,
          hr_signature_title: signatureTitle,
          hr_signature_date: new Date()?.toISOString(),
        })
        ?.eq('id', letterRequestId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        letterStatus: data?.letter_status,
        approvedAt: data?.approved_at,
      };
    } catch (error) {
      console.error('Error approving letter request:', error);
      throw error;
    }
  },

  async rejectLetterRequest(letterRequestId, hrManagerId, rejectionReason) {
    try {
      const { data, error } = await supabase
        ?.from('letter_requests')
        ?.update({
          letter_status: 'rejected',
          approved_by: hrManagerId,
          approved_at: new Date()?.toISOString(),
          rejection_reason: rejectionReason,
        })
        ?.eq('id', letterRequestId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        letterStatus: data?.letter_status,
        rejectionReason: data?.rejection_reason,
      };
    } catch (error) {
      console.error('Error rejecting letter request:', error);
      throw error;
    }
  },

  // ============================================================================
  // LETTER TEMPLATES
  // ============================================================================

  async getAllLetterTemplates() {
    try {
      const { data, error } = await supabase
        ?.from('letter_templates')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('letter_purpose', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        letterPurpose: row?.letter_purpose,
        templateName: row?.template_name,
        aiPromptTemplate: row?.ai_prompt_template,
        requiredFields: row?.required_fields,
        optionalFields: row?.optional_fields,
        letterFormat: row?.letter_format,
        includeCompanyLetterhead: row?.include_company_letterhead,
      })) || [];
    } catch (error) {
      console.error('Error fetching letter templates:', error);
      throw error;
    }
  },
};

export default letterRequestService;